import { ok, err, type Result } from '../core/result.js';

export interface StorageError {
  code: 'UNAVAILABLE' | 'PARSE_ERROR' | 'WRITE_ERROR' | 'QUOTA_EXCEEDED' | 'NOT_FOUND';
  message: string;
}

function getStore(kind: 'local' | 'session'): Storage | null {
  try {
    const s = kind === 'local' ? globalThis.localStorage : globalThis.sessionStorage;
    if (!s) return null;
    const testKey = '__reach_storage_probe__';
    s.setItem(testKey, '1');
    s.removeItem(testKey);
    return s;
  } catch {
    return null;
  }
}

export interface SafeStorage {
  available: boolean;
  get<T = unknown>(key: string): Result<T, StorageError>;
  getRaw(key: string): Result<string, StorageError>;
  set(key: string, value: unknown): Result<void, StorageError>;
  setRaw(key: string, value: string): Result<void, StorageError>;
  remove(key: string): Result<void, StorageError>;
  clear(prefix?: string): Result<void, StorageError>;
  keys(prefix?: string): string[];
}

function makeSafeStorage(kind: 'local' | 'session'): SafeStorage {
  const store = getStore(kind);
  const unavailable = err<StorageError>({ code: 'UNAVAILABLE', message: `${kind}Storage is not available` });

  return {
    available: store !== null,
    get<T>(key: string): Result<T, StorageError> {
      if (!store) return unavailable;
      const raw = store.getItem(key);
      if (raw === null) return err({ code: 'NOT_FOUND', message: `key "${key}" not found` });
      try {
        return ok(JSON.parse(raw) as T);
      } catch (e) {
        return err({ code: 'PARSE_ERROR', message: `failed to parse "${key}": ${(e as Error).message}` });
      }
    },
    getRaw(key: string): Result<string, StorageError> {
      if (!store) return unavailable;
      const raw = store.getItem(key);
      return raw === null ? err({ code: 'NOT_FOUND', message: `key "${key}" not found` }) : ok(raw);
    },
    set(key: string, value: unknown): Result<void, StorageError> {
      if (!store) return unavailable;
      try {
        store.setItem(key, JSON.stringify(value));
        return ok(undefined);
      } catch (e) {
        const msg = (e as Error).message;
        const quota = /quota/i.test(msg);
        return err({ code: quota ? 'QUOTA_EXCEEDED' : 'WRITE_ERROR', message: msg });
      }
    },
    setRaw(key: string, value: string): Result<void, StorageError> {
      if (!store) return unavailable;
      try {
        store.setItem(key, value);
        return ok(undefined);
      } catch (e) {
        const msg = (e as Error).message;
        const quota = /quota/i.test(msg);
        return err({ code: quota ? 'QUOTA_EXCEEDED' : 'WRITE_ERROR', message: msg });
      }
    },
    remove(key: string): Result<void, StorageError> {
      if (!store) return unavailable;
      try {
        store.removeItem(key);
        return ok(undefined);
      } catch (e) {
        return err({ code: 'WRITE_ERROR', message: (e as Error).message });
      }
    },
    clear(prefix?: string): Result<void, StorageError> {
      if (!store) return unavailable;
      try {
        if (prefix === undefined) {
          store.clear();
        } else {
          const toRemove: string[] = [];
          for (let i = 0; i < store.length; i++) {
            const k = store.key(i);
            if (k !== null && k.startsWith(prefix)) toRemove.push(k);
          }
          for (const k of toRemove) store.removeItem(k);
        }
        return ok(undefined);
      } catch (e) {
        return err({ code: 'WRITE_ERROR', message: (e as Error).message });
      }
    },
    keys(prefix?: string): string[] {
      if (!store) return [];
      const out: string[] = [];
      for (let i = 0; i < store.length; i++) {
        const k = store.key(i);
        if (k !== null && (prefix === undefined || k.startsWith(prefix))) out.push(k);
      }
      return out;
    }
  };
}

export const safeLocalStorage: SafeStorage = makeSafeStorage('local');
export const safeSessionStorage: SafeStorage = makeSafeStorage('session');
